<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ExtractGroupContactsModal from './ExtractGroupContactsModal.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const AI_MODES = ['normal', 'mention_only', 'never'];
const AI_MODE_LABEL_KEYS = {
  normal: 'INBOX_MGMT.WHATSAPP_GROUPS.AI_MODE_NORMAL',
  mention_only: 'INBOX_MGMT.WHATSAPP_GROUPS.AI_MODE_MENTION_ONLY',
  never: 'INBOX_MGMT.WHATSAPP_GROUPS.AI_MODE_NEVER',
};

const config = ref({
  default_ai_mode: 'mention_only',
  default_ignore: false,
  ai_trigger_keyword: 'IA',
  ...(props.inbox.whatsapp_group_config || {}),
  overrides: { ...(props.inbox.whatsapp_group_config?.overrides || {}) },
});
const groups = ref([]);
const isSaving = ref(false);

const aiModeOptions = computed(() =>
  AI_MODES.map(value => ({
    value,
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: t(AI_MODE_LABEL_KEYS[value]),
  }))
);

const overrideFor = jid => config.value.overrides[jid] || {};

const setIgnore = (jid, value) => {
  config.value.overrides[jid] = { ...overrideFor(jid), ignore: value };
};

const setAiMode = (jid, value) => {
  config.value.overrides[jid] = { ...overrideFor(jid), ai_mode: value };
};

// --- Radar de intenção: detecta quem PEDE o que a empresa vende ---
const DEFAULT_RADAR = {
  enabled: true,
  product: '',
  prefilter: [],
  confidence_threshold: 0.75,
};

const expandedRadarJid = ref(null);

const radarFor = jid => overrideFor(jid).radar || {};
const isRadarOn = jid => !!radarFor(jid).enabled;

const setRadar = (jid, patch) => {
  config.value.overrides[jid] = {
    ...overrideFor(jid),
    radar: { ...DEFAULT_RADAR, ...radarFor(jid), ...patch },
  };
};

const toggleRadar = (jid, value) => {
  setRadar(jid, { enabled: value });
  // Ligar sem configurar não detecta nada: abre o painel na hora.
  expandedRadarJid.value = value ? jid : null;
};

const toggleRadarPanel = jid => {
  expandedRadarJid.value = expandedRadarJid.value === jid ? null : jid;
};

// Termos separados por vírgula, sem espaço sobrando e sem vazio.
const prefilterText = jid => (radarFor(jid).prefilter || []).join(', ');
const setPrefilter = (jid, raw) => {
  const terms = raw
    .split(',')
    .map(term => term.trim().toLowerCase())
    .filter(Boolean);
  setRadar(jid, { prefilter: terms });
};

// Radar ligado sem produto ou sem termos não detecta nada — avisa em vez de
// deixar o usuário achar que está funcionando.
const radarIncomplete = jid =>
  isRadarOn(jid) &&
  (!radarFor(jid).product?.trim() || !(radarFor(jid).prefilter || []).length);

// --- Extração de contatos dos grupos ---
const selectedJids = ref([]);
const showExtractModal = ref(false);
const extractGroups = ref([]);

const isSelected = jid => selectedJids.value.includes(jid);
const toggleSelect = jid => {
  selectedJids.value = isSelected(jid)
    ? selectedJids.value.filter(j => j !== jid)
    : [...selectedJids.value, jid];
};
const allSelected = computed(
  () =>
    groups.value.length > 0 && selectedJids.value.length === groups.value.length
);
const toggleSelectAll = () => {
  selectedJids.value = allSelected.value ? [] : groups.value.map(g => g.jid);
};
const openExtract = groupList => {
  extractGroups.value = groupList;
  showExtractModal.value = true;
};
const extractSelected = () => {
  openExtract(groups.value.filter(g => selectedJids.value.includes(g.jid)));
};

const fetchGroups = async () => {
  try {
    const { data } = await InboxesAPI.getWhatsappGroups(props.inbox.id);
    groups.value = data;
  } catch {
    // groups stay empty — not a blocking error
  }
};

// Puxa TODOS os grupos que o número participa (mesmo sem mensagem no sistema).
const isSyncing = ref(false);
const syncGroups = async () => {
  isSyncing.value = true;
  try {
    const { data } = await InboxesAPI.syncWhatsappGroups(props.inbox.id);
    const synced = data.groups || [];
    const byJid = new Map();
    [...groups.value, ...synced].forEach(g => byJid.set(g.jid, g));
    groups.value = Array.from(byJid.values());
    useAlert(
      t('INBOX_MGMT.WHATSAPP_GROUPS.SYNC_SUCCESS', { count: synced.length })
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INBOX_MGMT.WHATSAPP_GROUPS.SYNC_ERROR')
    );
  } finally {
    isSyncing.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      whatsapp_group_config: config.value,
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_GROUPS.SAVED'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.WHATSAPP_GROUPS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchGroups);
</script>

<template>
  <div class="mx-8">
    <h4 class="text-2xl text-slate-800 dark:text-slate-100">
      {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.TITLE') }}
    </h4>
    <p class="text-slate-600 dark:text-slate-300">
      {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.SUBTITLE') }}
    </p>

    <section class="mt-6">
      <h5 class="font-medium">
        {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.DEFAULTS_TITLE') }}
      </h5>

      <label class="mt-3 block">
        {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.AI_MODE_LABEL') }}
        <select v-model="config.default_ai_mode">
          <option
            v-for="opt in aiModeOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </label>

      <label class="mt-3 flex items-center gap-2">
        <input v-model="config.default_ignore" type="checkbox" />
        {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.IGNORE_DEFAULT_LABEL') }}
      </label>

      <label class="mt-3 block">
        {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.KEYWORD_LABEL') }}
        <input v-model="config.ai_trigger_keyword" type="text" />
        <span class="text-xs text-slate-500">
          {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.KEYWORD_HELP') }}
        </span>
      </label>
    </section>

    <section class="mt-8">
      <div class="flex items-center justify-between">
        <h5 class="font-medium">
          {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.LIST_TITLE') }}
        </h5>
        <div class="flex items-center gap-2">
          <NextButton
            size="small"
            variant="secondary"
            :is-loading="isSyncing"
            @click="syncGroups"
          >
            <span class="i-lucide-refresh-cw mr-1" />
            {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.SYNC') }}
          </NextButton>
          <NextButton
            v-if="selectedJids.length"
            size="small"
            color="teal"
            @click="extractSelected"
          >
            <span class="i-lucide-contact-round mr-1" />
            {{
              $t('INBOX_MGMT.GROUP_EXTRACT.EXTRACT_SELECTED', {
                count: selectedJids.length,
              })
            }}
          </NextButton>
        </div>
      </div>
      <p v-if="!groups.length" class="text-slate-500">
        {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.EMPTY') }}
      </p>
      <table v-else class="mt-2 w-full">
        <thead>
          <tr>
            <th class="w-8">
              <input
                type="checkbox"
                :checked="allSelected"
                @change="toggleSelectAll"
              />
            </th>
            <th class="text-left">
              {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.COL_GROUP') }}
            </th>
            <th>{{ $t('INBOX_MGMT.WHATSAPP_GROUPS.COL_IGNORE') }}</th>
            <th>{{ $t('INBOX_MGMT.WHATSAPP_GROUPS.COL_AI') }}</th>
            <th>{{ $t('INBOX_MGMT.WHATSAPP_GROUPS.COL_RADAR') }}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <template v-for="group in groups" :key="group.jid">
            <tr>
              <td class="text-center">
                <input
                  type="checkbox"
                  :checked="isSelected(group.jid)"
                  @change="toggleSelect(group.jid)"
                />
              </td>
              <td>{{ group.name || group.jid }}</td>
              <td class="text-center">
                <input
                  type="checkbox"
                  :checked="
                    overrideFor(group.jid).ignore ?? config.default_ignore
                  "
                  @change="setIgnore(group.jid, $event.target.checked)"
                />
              </td>
              <td class="text-center">
                <select
                  :value="
                    overrideFor(group.jid).ai_mode ?? config.default_ai_mode
                  "
                  @change="setAiMode(group.jid, $event.target.value)"
                >
                  <option
                    v-for="opt in aiModeOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </td>
              <td class="text-center">
                <div class="flex items-center justify-center gap-1.5">
                  <input
                    type="checkbox"
                    :data-testid="`radar-toggle-${group.jid}`"
                    :checked="isRadarOn(group.jid)"
                    @change="toggleRadar(group.jid, $event.target.checked)"
                  />
                  <button
                    v-if="isRadarOn(group.jid)"
                    type="button"
                    class="text-xs text-n-blue-11 hover:underline"
                    :data-testid="`radar-config-${group.jid}`"
                    @click="toggleRadarPanel(group.jid)"
                  >
                    {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.RADAR_CONFIGURE') }}
                  </button>
                  <span
                    v-if="radarIncomplete(group.jid)"
                    v-tooltip.top="
                      $t('INBOX_MGMT.WHATSAPP_GROUPS.RADAR_INCOMPLETE')
                    "
                    class="i-lucide-alert-triangle size-4 text-n-amber-11"
                    :data-testid="`radar-incomplete-${group.jid}`"
                  />
                </div>
              </td>
              <td class="text-right">
                <NextButton
                  size="small"
                  variant="faded"
                  color="slate"
                  @click="openExtract([group])"
                >
                  {{ $t('INBOX_MGMT.GROUP_EXTRACT.EXTRACT') }}
                </NextButton>
              </td>
            </tr>
            <tr v-if="expandedRadarJid === group.jid">
              <td colspan="6" class="bg-n-alpha-1 px-4 py-3">
                <div class="flex flex-col gap-3">
                  <div>
                    <label
                      class="mb-1 block text-xs font-medium text-n-slate-11"
                    >
                      {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.RADAR_PRODUCT_LABEL') }}
                    </label>
                    <input
                      :value="radarFor(group.jid).product"
                      :data-testid="`radar-product-${group.jid}`"
                      class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                      :placeholder="
                        $t(
                          'INBOX_MGMT.WHATSAPP_GROUPS.RADAR_PRODUCT_PLACEHOLDER'
                        )
                      "
                      @input="
                        setRadar(group.jid, { product: $event.target.value })
                      "
                    />
                  </div>
                  <div>
                    <label
                      class="mb-1 block text-xs font-medium text-n-slate-11"
                    >
                      {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.RADAR_TERMS_LABEL') }}
                    </label>
                    <input
                      :value="prefilterText(group.jid)"
                      :data-testid="`radar-terms-${group.jid}`"
                      class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
                      :placeholder="
                        $t('INBOX_MGMT.WHATSAPP_GROUPS.RADAR_TERMS_PLACEHOLDER')
                      "
                      @input="setPrefilter(group.jid, $event.target.value)"
                    />
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.RADAR_TERMS_HINT') }}
                    </p>
                  </div>
                  <p class="text-xs text-n-slate-10">
                    {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.RADAR_EXPLAINER') }}
                  </p>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </section>

    <NextButton class="mt-6" :is-loading="isSaving" @click="save">
      {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.SAVE') }}
    </NextButton>

    <ExtractGroupContactsModal
      :show="showExtractModal"
      :inbox-id="inbox.id"
      :groups="extractGroups"
      @close="showExtractModal = false"
    />
  </div>
</template>
